import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

export default function Revocation() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="withdrawal-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/apply" className="nav-link">Apply</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 flex-1">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Withdrawal Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-8">AI Accountability Standards Council</p>
          
          <div className="prose prose-zinc max-w-none space-y-6 text-zinc-700">
            
            <div className="bg-zinc-100 border-l-4 border-zinc-400 p-4 mb-8">
              <p className="font-medium text-zinc-900">
                The AI Accountability Standards Council reserves the right to withdraw declarations at any time, without prior notice, for any violation of declaration terms or at its sole discretion.
              </p>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">1. Withdrawal Authority</h2>
              <p>
                Accountability declaration is a licensed signal granted by the AI Accountability Standards Council (AASC). This license may be withdrawn at any time. AASC maintains sole and final authority over all withdrawal decisions. No appeal process is guaranteed or implied.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">2. Withdrawal Triggers</h2>
              <p>
                Declaration may be withdrawn for reasons including, but not limited to:
              </p>
              
              <h3 className="text-lg font-medium text-zinc-900 mt-4 mb-2">False or Misleading Claims</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Misrepresentation of services, capabilities, or results</li>
                <li>False statements made during the application process</li>
                <li>Inaccurate information provided in attestations</li>
                <li>Deceptive marketing or advertising practices</li>
              </ul>

              <h3 className="text-lg font-medium text-zinc-900 mt-4 mb-2">Misuse of the Declaration Mark</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Unauthorized modification of declaration marks or badges</li>
                <li>Use of declaration marks after expiration or withdrawal</li>
                <li>Implying broader scope than declared</li>
                <li>Associating the mark with unapproved products or services</li>
              </ul>

              <h3 className="text-lg font-medium text-zinc-900 mt-4 mb-2">Material Misrepresentation of Scope</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Claiming declaration covers services not attested</li>
                <li>Misrepresenting the nature or meaning of declaration</li>
                <li>Implying regulatory approval or endorsement</li>
                <li>Suggesting audit or inspection occurred when none did</li>
              </ul>

              <h3 className="text-lg font-medium text-zinc-900 mt-4 mb-2">Other Grounds</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Failure to maintain eligibility requirements</li>
                <li>Non-payment of declaration fees</li>
                <li>Conduct detrimental to the integrity of the declaration program</li>
                <li>Violation of Terms of Service</li>
                <li>Any other reason at AASC's sole discretion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">3. Withdrawal Process</h2>
              <p>
                Upon determination that withdrawal is warranted:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Declaration status is immediately changed to "Withdrawn" in the Registry</li>
                <li>The organization must immediately cease use of all AASC declaration marks</li>
                <li>Withdrawal date and reason are publicly displayed in the Registry</li>
                <li>No refund of declaration fees is provided</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">4. Permanent Record</h2>
              <p>
                Withdrawn declarations are not removed from the Registry. The public record permanently reflects:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Organization name</li>
                <li>Original declaration date</li>
                <li>Withdrawal date</li>
                <li>Withdrawal reason</li>
                <li>Status as "Withdrawn"</li>
              </ul>
              <p className="mt-3">
                This permanent record serves the public interest by maintaining transparency and accountability in the declaration program.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">5. Post-Withdrawal</h2>
              <p>
                Following withdrawal:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>The organization may not reapply for declaration for a minimum of 12 months</li>
                <li>Future applications may be denied based on prior withdrawal history</li>
                <li>Continued use of declaration marks after withdrawal may result in legal action</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">6. No Appeal</h2>
              <p>
                AASC is under no obligation to provide an appeal process for withdrawal decisions. Withdrawal decisions are final. AASC is not obligated to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide advance notice of withdrawal</li>
                <li>Disclose investigation details</li>
                <li>Offer opportunity to cure violations</li>
                <li>Reconsider withdrawal decisions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">7. Reporting Violations</h2>
              <p>
                Third parties may report suspected violations to{' '}
                <a href="mailto:enforcement@aiaccountabilitycouncil.com" className="text-zinc-900 underline">
                  enforcement@aiaccountabilitycouncil.com
                </a>
                . AASC reviews reports at its discretion and is not obligated to act on every report or disclose outcomes.
              </p>
            </section>

            <section className="bg-zinc-100 p-6 mt-8">
              <h2 className="text-lg font-semibold text-zinc-900 mb-2">Related Policies</h2>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/terms" className="text-zinc-600 hover:text-zinc-900 underline">Terms of Service</Link>
                <Link to="/enforcement" className="text-zinc-600 hover:text-zinc-900 underline">Enforcement Policy</Link>
                <Link to="/declaration-scope" className="text-zinc-600 hover:text-zinc-900 underline">Declaration Scope</Link>
                <Link to="/eligibility" className="text-zinc-600 hover:text-zinc-900 underline">Eligibility Requirements</Link>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Contact</h2>
              <p>
                Questions regarding this policy may be directed to{' '}
                <a href="mailto:contact@aiaccountabilitycouncil.com" className="text-zinc-900 underline">contact@aiaccountabilitycouncil.com</a>
                {' '}or through the{' '}
                <Link to="/contact" className="text-zinc-900 underline">contact form</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
